import React, { useState } from 'react';
import NavigationBar from './NavigationBar';
import './StressCalculator.css';

interface StressResult {
  stress: number;
  safetyFactor: number;
  status: 'safe' | 'warning' | 'danger';
}

const StressCalculator: React.FC = () => {
  const [force, setForce] = useState<string>('');
  const [area, setArea] = useState<string>('');
  const [yieldStrength, setYieldStrength] = useState<string>('');
  const [result, setResult] = useState<StressResult | null>(null);

  const calculateStress = () => {
    const f = parseFloat(force);
    const a = parseFloat(area);
    const ys = parseFloat(yieldStrength);

    if (f && a && ys && a > 0 && ys > 0) {
      const stress = f / a;
      const safetyFactor = ys / stress;

      let status: 'safe' | 'warning' | 'danger';
      if (safetyFactor >= 3) {
        status = 'safe';
      } else if (safetyFactor >= 1.5) {
        status = 'warning';
      } else {
        status = 'danger';
      }

      setResult({
        stress,
        safetyFactor,
        status,
      });
    }
  };

  const getStatusColor = (status: 'safe' | 'warning' | 'danger') => {
    switch (status) {
      case 'safe':
        return '#4CAF50';
      case 'warning':
        return '#FF9800';
      case 'danger':
        return '#F44336';
      default:
        return '#666';
    }
  };

  const getStatusText = (status: 'safe' | 'warning' | 'danger') => {
    switch (status) {
      case 'safe':
        return '安全';
      case 'warning':
        return '警告';
      case 'danger':
        return '危险';
      default:
        return '未知';
    }
  };

  return (
    <div className="stress-calculator">
      <NavigationBar title="材料应力计算器" />

      <div className="input-group">
        <label>
          作用力 (N):
          <input
            type="number"
            value={force}
            onChange={e => setForce(e.target.value)}
            placeholder="输入作用力"
          />
        </label>
      </div>

      <div className="input-group">
        <label>
          横截面积 (mm²):
          <input
            type="number"
            value={area}
            onChange={e => setArea(e.target.value)}
            placeholder="输入横截面积"
          />
        </label>
      </div>

      <div className="input-group">
        <label>
          屈服强度 (MPa):
          <input
            type="number"
            value={yieldStrength}
            onChange={e => setYieldStrength(e.target.value)}
            placeholder="输入材料屈服强度"
          />
        </label>
      </div>

      <button onClick={calculateStress} className="calculate-btn">
        计算应力
      </button>

      {result && (
        <div className="result">
          <h3>计算结果</h3>
          <div className="result-item">
            <span>应力:</span>
            <span>{result.stress.toFixed(2)} MPa</span>
          </div>
          <div className="result-item">
            <span>安全系数:</span>
            <span>{result.safetyFactor.toFixed(2)}</span>
          </div>
          <div className="result-item">
            <span>安全状态:</span>
            <span
              className="status-indicator"
              style={{ color: getStatusColor(result.status) }}
            >
              {getStatusText(result.status)}
            </span>
          </div>
        </div>
      )}

      <div className="info-section">
        <h4>应力计算公式</h4>
        <p>σ = F / A</p>
        <p>其中：σ = 应力 (MPa), F = 作用力 (N), A = 横截面积 (mm²)</p>

        <h4>安全系数</h4>
        <p>n = σy / σ</p>
        <p>其中：n = 安全系数, σy = 屈服强度 (MPa), σ = 计算应力 (MPa)</p>
      </div>
    </div>
  );
};

export default StressCalculator;
